/**
 * "Which flight search engines load the fastest?": the October 2026 speed
 * benchmark, as plain data. The guide page renders it, its FAQs become the
 * page's FAQPage data (src/content/seo/pages.js), and the pre-renderer writes
 * the same article into the page's static HTML (scripts/prerender-help.mjs),
 * so crawlers that don't run JavaScript read every word too.
 *
 * Numbers come from the benchmark runs of Oct 20, 2026: each site's mean time
 * to the first flight result with a fare, averaged over six routes (the mean
 * of route means). Don't round or reorder them without re-running it.
 */

export const SPEED_GUIDE_PATH = '/guides/fastest-flight-search-engines';
export const SPEED_GUIDE_PUBLISHED = '2026-10-20';

export const SPEED_GUIDE = {
  kicker: 'Travel · Benchmark',
  title: 'Which flight search engines load the fastest?',
  dek: 'We timed how long the most popular flight search engines take to show results after you hit Search, on routes from short domestic hops to a transatlantic flight.',
  updated: 'October 20, 2026',
  author: 'Stephanie Ludwig',
};

export const ROUTES = [
  { code: 'LAX-DFW', label: 'Los Angeles to Dallas–Fort Worth', kind: 'Popular domestic' },
  { code: 'ATL-MCO', label: 'Atlanta to Orlando', kind: 'Popular domestic' },
  { code: 'JFK-LAX', label: 'New York to Los Angeles', kind: 'Cross-country' },
  { code: 'JFK-LHR', label: 'New York to London Heathrow', kind: 'Transatlantic' },
  { code: 'MIA-CUN', label: 'Miami to Cancún', kind: 'Short international' },
  { code: 'SEA-BOI', label: 'Seattle to Boise', kind: 'Regional' },
];

/** Seconds to first results: overall (mean of route means), then per route in ROUTES order. */
export const RESULTS = [
  { site: 'Google Flights', avg: 0.82, routes: [0.71, 0.97, 0.67, 1.00, 0.77, 0.82] },
  { site: 'Defera', avg: 1.68, routes: [1.19, 1.77, 1.38, 2.81, 1.60, 1.34] },
  { site: 'Kayak', avg: 2.51, routes: [2.06, 2.23, 2.50, 2.37, 2.51, 3.42] },
  { site: 'Cheapflights', avg: 2.57, routes: [2.25, 2.30, 2.92, 2.69, 2.64, 2.64] },
  { site: 'Kiwi.com', avg: 2.63, routes: [3.15, 2.31, 2.60, 2.58, 3.13, 1.98] },
  { site: 'Momondo', avg: 2.72, routes: [2.45, 2.38, 3.01, 3.25, 2.14, 3.09] },
  { site: 'Skyscanner', avg: 2.75, routes: [2.66, 2.47, 2.58, 3.38, 2.84, 2.57] },
  { site: 'Rift', avg: 4.03, routes: [3.85, 2.82, 3.41, 5.32, 3.28, 5.51] },
  { site: 'Skiplagged', avg: 5.11, routes: [5.02, 3.95, 6.17, 4.76, 5.85, 4.89] },
  { site: 'Expedia', avg: 7.24, routes: [7.75, 4.21, 6.60, 9.07, 5.66, 10.15] },
  { site: 'Alternative Airlines', avg: 8.19, routes: [6.05, 5.55, 10.19, 13.42, 4.31, 9.60] },
  { site: 'Travelocity', avg: 8.52, routes: [11.58, 6.49, 8.20, 9.07, 6.09, 9.70] },
  { site: 'Orbitz', avg: 8.56, routes: [10.93, 6.30, 8.17, 10.23, 6.27, 9.44] },
  { site: 'Hopper', avg: 9.79, routes: [9.21, 9.22, 8.06, 16.00, 6.70, 9.56] },
  { site: 'PayLaterTravel', avg: 10.19, routes: [11.08, 10.47, 10.71, 13.43, 9.01, 6.46] },
  { site: 'Trip.com', avg: 12.35, routes: [9.88, 13.75, 12.68, 14.56, 11.26, 11.98] },
  { site: 'Booking.com', avg: 13.34, routes: [12.53, 15.25, 11.77, 16.96, 11.21, 12.34] },
  { site: 'CheapOair', avg: 15.06, routes: [14.97, 16.27, 13.20, 20.74, 13.31, 11.86] },
  { site: 'Priceline', avg: 16.65, routes: [14.89, 15.52, 15.85, 20.49, 17.54, 15.64] },
];

export const KEY_FINDINGS = [
  'Google Flights was the fastest, showing its first results in 0.82 seconds on average, and it averaged one second or less on every route.',
  'Defera came second at 1.68 seconds, ahead of every metasearch engine and online travel agency we tested.',
  'Kayak, Cheapflights, Kiwi.com, Momondo and Skyscanner formed a close pack between 2.5 and 2.8 seconds.',
  'The big online travel agencies were much slower: Expedia averaged 7.24 seconds, Booking.com 13.34 seconds and Priceline 16.65 seconds.',
  'From fastest to slowest, the gap was about 20 times: under a second on Google Flights versus more than 16 seconds on Priceline.',
  'Long-haul searches were the slowest for almost everyone. New York to London added time on most sites, while short domestic routes were quickest.',
];

/** Body sections: [heading, paragraphs]. */
export const SECTIONS = [
  ['Why search speed matters', [
    'Comparing flights means running a lot of searches: different dates, nearby airports, one-way versus round trip. A site that takes 10 to 15 seconds per search turns a quick comparison into a long one.',
    'Speed is not the same as price. This benchmark measures how quickly results appear, not which site has the cheapest fare, so it is worth comparing prices on more than one site before you book.',
  ]],
  ['What separates the fast sites from the slow ones', [
    'In our screen recordings, the fastest engines showed the first flights as soon as they had them and kept adding more while the page was open. Several of the slower sites held the page on a loading screen until a full set of fares was ready.',
    'Results also varied by route. Popular domestic routes such as Atlanta to Orlando were quick almost everywhere, while the transatlantic search from New York to London was the slowest route for most sites.',
  ]],
];

export const METHODOLOGY = [
  'We measured the time from submitting a search to the first rendered itinerary with a fare on screen, defined as at least two flights with departure times and prices visible and not covered by a loading screen.',
  'Each site was searched various times on each of six round-trip routes (LAX–DFW, ATL–MCO, JFK–LAX, JFK–LHR, MIA–CUN and SEA–BOI) for one adult in economy, with April 2027 departures. Every site searched the same dates in each round, and each round used dates that had not been searched before, so no site could answer from a recent identical search.',
  'A site’s score is the mean of its six route averages, so every route counts equally. Tests ran in desktop Chrome on macOS with a warm browser cache on October 20, 2026.',
  'Other sites were timed by opening their results page directly, which is what their Search button does. Defera was timed from the Search click inside its app; timed the same way as the others, by opening its results page, it averaged 2.08 seconds and still ranked second.',
  'Speeds vary with network, location and time of day, so treat the figures as a snapshot from one setup rather than a guarantee.',
];

export const SPEED_GUIDE_FAQS = [
  {
    q: 'What is the fastest flight search engine?',
    a: 'In our October 2026 benchmark, Google Flights was the fastest, showing its first flight results in 0.82 seconds on average across six routes. Defera was second at 1.68 seconds, followed by Kayak at 2.51 seconds.',
  },
  {
    q: 'Is Kayak faster than Expedia?',
    a: 'Yes. Kayak averaged 2.51 seconds to show its first results, while Expedia averaged 7.24 seconds, making Kayak almost three times faster in our test.',
  },
  {
    q: 'Which flight search sites were the slowest?',
    a: 'Priceline was the slowest at 16.65 seconds on average, followed by CheapOair at 15.06 seconds and Booking.com at 13.34 seconds.',
  },
  {
    q: 'Does a faster flight search mean cheaper flights?',
    a: 'No. This benchmark only measures how quickly results appear, not fares. Prices can differ between sites, so compare a few before you book.',
  },
  {
    q: 'How was the flight search speed test done?',
    a: 'Each of 19 sites was searched various times on each of six round-trip routes in desktop Chrome on October 20, 2026. We timed how long each took to show at least two flights with prices, and averaged the results by route.',
  },
];

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const fmt = (n) => n.toFixed(2);

/** The whole article as static HTML, for the pre-rendered page. */
export function speedGuideHtml() {
  return [
    '<nav aria-label="Breadcrumb"><a href="/">Home</a> › <a href="/guides">Guides</a></nav>',
    '<article>',
    `<p>${esc(SPEED_GUIDE.kicker)}</p>`,
    `<h1>${esc(SPEED_GUIDE.title)}</h1>`,
    `<p>${esc(SPEED_GUIDE.dek)}</p>`,
    `<p class="byline">By ${esc(SPEED_GUIDE.author)} · Updated ${esc(SPEED_GUIDE.updated)}</p>`,
    '<h2>Key findings</h2>',
    `<ul>${KEY_FINDINGS.map((f) => `<li>${esc(f)}</li>`).join('')}</ul>`,
    '<h2>Results: average seconds to first flight results</h2>',
    '<table><thead><tr><th>Rank</th><th>Site</th><th>Seconds</th></tr></thead><tbody>',
    RESULTS.map((r, i) => `<tr><td>${i + 1}</td><td>${esc(r.site)}</td><td>${fmt(r.avg)}</td></tr>`).join(''),
    '</tbody></table>',
    '<h2>Results by route</h2>',
    `<table><thead><tr><th>Site</th>${ROUTES.map((r) => `<th>${esc(r.code)}</th>`).join('')}</tr></thead><tbody>`,
    RESULTS.map((r) => `<tr><td>${esc(r.site)}</td>${r.routes.map((s) => `<td>${fmt(s)}</td>`).join('')}</tr>`).join(''),
    '</tbody></table>',
    ...SECTIONS.flatMap(([h, ps]) => [`<h2>${esc(h)}</h2>`, ...ps.map((p) => `<p>${esc(p)}</p>`)]),
    '<h2>How we tested</h2>',
    ...METHODOLOGY.map((p) => `<p>${esc(p)}</p>`),
    '<h2>Frequently asked questions</h2>',
    ...SPEED_GUIDE_FAQS.map((f) => `<h3>${esc(f.q)}</h3><p>${esc(f.a)}</p>`),
    '<p><a href="/search">Search flights on Defera</a> · <a href="/guides">More travel guides</a></p>',
    '</article>',
  ].join('\n');
}
